/**
 * TalkToast Icon Library
 *
 * Centralized icon system for consistent UI across all platforms.
 * Uses Lucide React for professional, customizable icons.
 *
 * Usage:
 * ```tsx
 * import { ICONS, Icon, getIconSize, getIconColor } from '@talktoast/shared'
 *
 * // Direct usage
 * <ICONS.navigation.home className={getIconSize('md')} />
 *
 * // With helper component
 * <Icon icon={ICONS.actions.search} size="sm" color="primary" />
 * ```
 *
 * @see docs/Brand Guide v0.md - Section 5: Iconography & Illustration
 */

import {
  // Navigation & Core
  Home,
  Calendar,
  CalendarDays,
  Plus,
  Settings,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Menu,
  ShoppingBag,

  // User & Profile
  User,
  Users,
  Bell,
  LogOut,
  Eye,

  // Actions & Interactions
  Search,
  Download,
  Share2,
  Check,
  X,
  Star,
  ThumbsUp,
  ThumbsDown,
  Trash2,
  Pencil,
  MoreVertical,
  RefreshCw,
  Send,

  // Food & Meal Related
  ChefHat,
  Utensils,
  ShoppingCart,
  Apple,
  Carrot,
  Beef,
  Fish,
  Milk,
  Package,
  Salad,
  Snowflake,
  Wheat,
  Coffee,

  // Feedback & Status
  AlertCircle,
  AlertTriangle,

  // Time & Planning
  Clock,

  // Features & Magic
  Lightbulb,
  Sparkles,
  Globe,
  Sliders,
  MessageCircle,
  Heart,

  type LucideIcon
} from 'lucide-react'

/**
 * Icon Categories
 * Organized by functional purpose for easy discovery
 */

// =============================================================================
// NAVIGATION ICONS - App-wide navigation and routing
// =============================================================================
export const NAVIGATION_ICONS = {
  home: Home,
  calendar: Calendar,
  calendarDays: CalendarDays,
  add: Plus,
  settings: Settings,
  back: ChevronLeft,
  forward: ChevronRight,
  shoppingBag: ShoppingBag,
} as const

// =============================================================================
// USER & PROFILE ICONS - User-related actions and information
// =============================================================================
export const USER_ICONS = {
  profile: User,
  users: Users,
  notifications: Bell,
  logout: LogOut,
  view: Eye,
} as const

// =============================================================================
// ACTION ICONS - Interactive actions users can perform
// =============================================================================
export const ACTION_ICONS = {
  search: Search,
  download: Download,
  share: Share2,
  favorite: Star,
  like: ThumbsUp,
  dislike: ThumbsDown,
  close: X,
  confirm: Check,
  check: Check,
  expand: ChevronDown,
  collapse: ChevronUp,
  delete: Trash2,
  edit: Pencil,
  message: MessageCircle,
  time: Clock,
  add: Plus,
  more: MoreVertical,
  refresh: RefreshCw,
  send: Send,
} as const

// =============================================================================
// FOOD & MEAL ICONS - Meal planning and food-related features
// =============================================================================
export const FOOD_ICONS = {
  chef: ChefHat,
  utensils: Utensils,
  cart: ShoppingCart,
  fruit: Apple,
  heart: Heart,
} as const

// =============================================================================
// SHOPPING ICONS - Store sections and shopping list categories
// =============================================================================
export const SHOPPING_ICONS = {
  produce: Carrot,
  meat: Beef,
  seafood: Fish,
  dairy: Milk,
  pantry: Package,
  spices: Salad,
  frozen: Snowflake,
  bakery: Wheat,
  beverages: Coffee,
  other: ShoppingCart,
} as const

// =============================================================================
// FEEDBACK ICONS - Status indicators and user feedback
// =============================================================================
export const FEEDBACK_ICONS = {
  info: AlertCircle,
  warning: AlertTriangle,
  error: X,
  success: Check,
} as const

// =============================================================================
// UTILITY ICONS - Supporting UI elements
// =============================================================================
export const UTILITY_ICONS = {
  clock: Clock,
  idea: Lightbulb,
  magic: Sparkles,
  language: Globe,
  filters: Sliders,
  menu: Menu,
  x: X,
  messageCircle: MessageCircle,
  heart: Heart,
} as const

// =============================================================================
// MEAL TYPE EMOJIS - Food-based emojis for meal type representation
// =============================================================================
/**
 * Meal type emojis using food-based icons (more universal than time-of-day)
 * These can be replaced with custom SVG icons in the future while maintaining
 * the same interface.
 *
 * Design Decision: Food-based emojis (🍳🥗🍽️) over time-based (🌅☀️🌙)
 * - More universal across cultures/timezones
 * - Better appetite appeal
 * - Clearer food-related intent
 */
export const MEAL_TYPE_EMOJIS = {
  breakfast: '🍳',  // Fried egg - iconic breakfast food
  lunch: '🥗',      // Salad - light, fresh midday meal
  dinner: '🍽️',     // Plate with cutlery - formal main meal
  snacks: '🍿',     // Popcorn - casual snacking
  dessert: '🍰',    // Cake slice - sweet treat
} as const

export type MealTypeKey = keyof typeof MEAL_TYPE_EMOJIS

// =============================================================================
// TIME ICONS - Time and scheduling related icons
// =============================================================================
export const TIME_ICONS = {
  clock: Clock,
} as const

// =============================================================================
// UNIFIED ICON LIBRARY - All icons in one object for convenience
// =============================================================================
export const ICONS = {
  navigation: NAVIGATION_ICONS,
  user: USER_ICONS,
  actions: ACTION_ICONS,
  food: FOOD_ICONS,
  shopping: SHOPPING_ICONS,
  feedback: FEEDBACK_ICONS,
  utility: UTILITY_ICONS,
  time: TIME_ICONS,
  mealTypes: MEAL_TYPE_EMOJIS,
} as const

// =============================================================================
// ICON SIZING SYSTEM - Standardized sizes for consistent UI
// =============================================================================
export const ICON_SIZES = {
  xs: 'w-3 h-3',       // 12px - Very small, inline with small text
  sm: 'w-4 h-4',       // 16px - Small buttons, compact UI
  md: 'w-5 h-5',       // 20px - Default size, most common
  lg: 'w-6 h-6',       // 24px - Emphasis, larger touch targets
  xl: 'w-8 h-8',       // 32px - Hero icons, prominent features
  '2xl': 'w-12 h-12',  // 48px - Extra large, empty states
} as const

export type IconSize = keyof typeof ICON_SIZES

// =============================================================================
// ICON COLOR SYSTEM - Semantic colors aligned with design tokens
// =============================================================================
export const ICON_COLORS = {
  // Brand colors
  primary: 'text-toast-gold-700',
  brand: 'text-toast-gold-600',

  // Neutral colors
  default: 'text-gray-600',
  muted: 'text-gray-400',
  dark: 'text-gray-900',
  white: 'text-white',

  // Semantic colors
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-error',

  // Interactive states
  hover: 'hover:text-toast-gold-700',
  active: 'text-toast-gold-800',
} as const

export type IconColor = keyof typeof ICON_COLORS

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get standardized icon size class
 * @param size - Icon size key
 * @returns Tailwind classes for icon dimensions
 */
export const getIconSize = (size: IconSize = 'md'): string => {
  return ICON_SIZES[size]
}

/**
 * Get semantic icon color class
 * @param color - Icon color key
 * @returns Tailwind class for icon color
 */
export const getIconColor = (color: IconColor = 'default'): string => {
  return ICON_COLORS[color]
}

/**
 * Get combined icon classes (size + color + custom)
 * @param size - Icon size
 * @param color - Icon color
 * @param className - Additional custom classes
 * @returns Combined Tailwind classes
 */
export const getIconClasses = (
  size: IconSize = 'md',
  color?: IconColor,
  className?: string
): string => {
  const sizeClass = getIconSize(size)
  const colorClass = color ? getIconColor(color) : ''
  return `${sizeClass} ${colorClass} ${className || ''}`.trim()
}

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type { LucideIcon }

/**
 * Type helper for icon component props
 */
export interface IconProps {
  icon: LucideIcon
  size?: IconSize
  color?: IconColor
  className?: string
  onClick?: () => void
  'aria-label'?: string
}
